'use client'

import * as React from 'react'
import { LazyMotion, domAnimation, m, useInView, useReducedMotion } from 'framer-motion'
import { Check, ShieldAlert, CheckCircle2, PauseCircle, XCircle } from 'lucide-react'
import { EASE, SPRING } from '@/lib/motion'

// Motion Wave D set-piece for /moderation: the reported-content queue
// mock, extracted verbatim from the page so the server component stays a
// server component. The figure settles up once when in view, queue rows
// slide in left-to-right with a short stagger, and the rights + action
// chips pop on a spring just after their row lands. Table semantics are
// kept intact (m.tr animates the real <tr>). Plays once; reduced motion
// renders the final state instantly.
//
// Clearly representative sample rows for the moderation-queue mock.
// Product names are the canonical Idukki marketing-shot SKUs from
// CLAUDE.md. Handles, reasons and states are illustrative sample data,
// NOT a real customer's content, and labelled as such on the surface.
type QueueState = 'approve' | 'hold' | 'remove'

const QUEUE: Array<{
  handle: string
  product: string
  reason: string
  rights: 'Cleared' | 'Requested' | 'None'
  state: QueueState
}> = [
  { handle: '@maya.k',    product: 'Airlift Overcoat Brown',        reason: 'Flagged: competitor logo in frame', rights: 'Cleared',   state: 'hold'    },
  { handle: '@liv.j',     product: 'White Sweater Green Stripes',   reason: 'Reported by visitor: off-brand',    rights: 'Requested', state: 'hold'    },
  { handle: '@iris.skin', product: 'Bodycon Sunscreen SPF 50',     reason: 'Auto-flag: unverified health claim', rights: 'None',      state: 'remove'  },
  { handle: '@run.club',  product: 'Tennis Cardigan Spring/Summer', reason: 'Clean: rights cleared, on-brand',   rights: 'Cleared',   state: 'approve' },
  { handle: '@store.live', product: 'Women Oval Sunglasses',        reason: 'Reported by visitor: wrong product', rights: 'Cleared',   state: 'approve' },
]

const STATE_META: Record<QueueState, { label: string; icon: typeof CheckCircle2; cls: string }> = {
  approve: { label: 'Approve', icon: CheckCircle2, cls: 'bg-emerald-500/12 text-emerald-700' },
  hold:    { label: 'On hold', icon: PauseCircle,  cls: 'bg-amber-500/15 text-amber-700' },
  remove:  { label: 'Remove',  icon: XCircle,      cls: 'bg-destructive/12 text-destructive' },
}

const RIGHTS_META: Record<'Cleared' | 'Requested' | 'None', string> = {
  Cleared:   'bg-emerald-500/12 text-emerald-700',
  Requested: 'bg-primary/10 text-primary',
  None:      'bg-muted text-muted-foreground',
}

export function ModerationQueueMotion() {
  const ref = React.useRef<HTMLElement>(null)
  const inView = useInView(ref, { once: true, margin: '0px 0px -15% 0px' })
  const reduce = useReducedMotion()
  const done = inView // reduced motion jumps straight to the final state

  return (
    <LazyMotion features={domAnimation}>
      <m.figure
        ref={ref}
        className="mt-12 overflow-hidden rounded-2xl border bg-card shadow-sm"
        initial={reduce ? false : { opacity: 0, y: 16 }}
        animate={done ? { opacity: 1, y: 0 } : undefined}
        transition={{ duration: 0.45, ease: EASE }}
      >
        <figcaption className="flex flex-wrap items-center justify-between gap-3 border-b bg-secondary/40 px-5 py-4">
          <div className="flex items-center gap-2">
            <ShieldAlert className="h-4 w-4 text-primary" aria-hidden="true" />
            <span className="font-display text-sm font-semibold">Reported content queue</span>
            <span className="rounded-full border bg-card px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
              Sample data
            </span>
          </div>
          <span className="text-xs text-muted-foreground">5 awaiting review · rights gate on</span>
        </figcaption>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[680px] text-left text-sm">
            <thead>
              <tr className="border-b text-[11px] uppercase tracking-wider text-muted-foreground">
                <th scope="col" className="px-5 py-3 font-semibold">Source</th>
                <th scope="col" className="px-5 py-3 font-semibold">Tagged product</th>
                <th scope="col" className="px-5 py-3 font-semibold">Why it surfaced</th>
                <th scope="col" className="px-5 py-3 font-semibold">Rights</th>
                <th scope="col" className="px-5 py-3 text-right font-semibold">Suggested action</th>
              </tr>
            </thead>
            <tbody>
              {QUEUE.map((row, i) => {
                const s = STATE_META[row.state]
                const StateIcon = s.icon
                const rowDelay = 0.15 + i * 0.07
                return (
                  <m.tr
                    key={row.handle + row.product}
                    className="border-b last:border-0 transition-colors hover:bg-secondary/30"
                    initial={reduce ? false : { opacity: 0, x: -12 }}
                    animate={done ? { opacity: 1, x: 0 } : undefined}
                    transition={{ duration: 0.35, ease: EASE, delay: reduce ? 0 : rowDelay }}
                  >
                    <th scope="row" className="px-5 py-4 font-medium text-foreground">{row.handle}</th>
                    <td className="px-5 py-4 text-muted-foreground">{row.product}</td>
                    <td className="px-5 py-4 text-muted-foreground">{row.reason}</td>
                    <td className="px-5 py-4">
                      <m.span
                        className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold ${RIGHTS_META[row.rights]}`}
                        initial={reduce ? false : { opacity: 0, scale: 0.8 }}
                        animate={done ? { opacity: 1, scale: 1 } : undefined}
                        transition={reduce ? { duration: 0 } : { ...SPRING, delay: rowDelay + 0.12 }}
                      >
                        {row.rights}
                      </m.span>
                    </td>
                    <td className="px-5 py-4">
                      <m.span
                        className={`ml-auto flex w-fit items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold ${s.cls}`}
                        initial={reduce ? false : { opacity: 0, scale: 0.8 }}
                        animate={done ? { opacity: 1, scale: 1 } : undefined}
                        transition={reduce ? { duration: 0 } : { ...SPRING, delay: rowDelay + 0.16 }}
                      >
                        <StateIcon className="h-3.5 w-3.5" aria-hidden="true" />
                        {s.label}
                      </m.span>
                    </td>
                  </m.tr>
                )
              })}
            </tbody>
          </table>
        </div>
        <div className="flex items-center justify-between gap-3 border-t bg-secondary/40 px-5 py-3 text-xs text-muted-foreground">
          <span>5 of 5 flagged items shown</span>
          <span className="inline-flex items-center gap-1.5">
            <Check className="h-3.5 w-3.5 text-emerald-600" aria-hidden="true" />
            Rights-gated before publish
          </span>
        </div>
      </m.figure>
    </LazyMotion>
  )
}
